import { useState, useMemo, useRef, useEffect } from "react";
import { Editor } from "./editor/Editor";
import { ResultTable, formatCellValue } from "./results/ResultTable";
import { Loading } from "./common/Loading";
import { Play, CheckCheck, RefreshCw } from "lucide-react";
import { API_HOST } from "../constants";
import type { QueryStatus } from "../types";

interface InlineSQLCheckProps {
  initialSql: string;
  checks: Array<Record<string, any>>; // Массив проверок в формате из YAML
  user: { id: number } | null;
}

interface QueryResponse {
  rows: unknown[][];
  columns: string[];
  error_msg?: string;
  status?: boolean;
  decision?: string;
  execution_time_ms?: number;
  memory_usage_bytes?: number;
  read_bytes?: number;
  read_rows?: number;
  written_bytes?: number;
  written_rows?: number;
  result_bytes?: number;
  result_rows?: number;
  cpu_time_us?: number;
}

const formatRows = (rows: unknown[][], columns: string[]): Record<string, unknown>[] => {
  if (!rows || rows.length === 0) return [];
  return rows.map((row) => {
    const obj: Record<string, unknown> = {};
    columns.forEach((col, idx) => {
      obj[col] = row[idx];
    });
    return obj;
  });
};

const formatExecutionTime = (ms: number | null): string | null => {
  if (ms === null || ms === undefined) return null;
  if (ms < 1) return `${(ms * 1000).toFixed(0)}μs`;
  if (ms < 1000) return `${ms.toFixed(2)}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
};

const formatBytes = (bytes: number | null): string | null => {
  if (bytes === null || bytes === undefined) return null;
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)}KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(2)}MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)}GB`;
};

const formatRowsCount = (rows: number | null): string | null => {
  if (rows === null || rows === undefined) return null;
  if (rows < 1000) return `${rows}`;
  if (rows < 1000000) return `${(rows / 1000).toFixed(1)}K`;
  return `${(rows / 1000000).toFixed(1)}M`;
};

export const InlineSQLCheck = ({ initialSql = "", checks = [], user }: InlineSQLCheckProps) => {
  const [sql, setSql] = useState(initialSql);
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const [result, setResult] = useState<Record<string, unknown>[]>([]);
  const [resultColumns, setResultColumns] = useState<string[]>([]);
  const [executionTimeMs, setExecutionTimeMs] = useState<number | null>(null);
  const [memoryUsageBytes, setMemoryUsageBytes] = useState<number | null>(null);
  const [readBytes, setReadBytes] = useState<number | null>(null);
  const [readRows, setReadRows] = useState<number | null>(null);
  const [cpuTimeUs, setCpuTimeUs] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const editorBlockRef = useRef<HTMLDivElement>(null);

  // Колёсико мыши над блоком SQL прокручивает страницу урока, а не редактор (как в чате).
  useEffect(() => {
    const el = editorBlockRef.current;
    if (!el) return;
    const handler = (e: WheelEvent) => {
      const scrollParent = (e.target as HTMLElement).closest?.("[data-lesson-scroll]") as HTMLElement | null;
      if (scrollParent) {
        e.preventDefault();
        scrollParent.scrollTop += e.deltaY;
      }
    };
    el.addEventListener("wheel", handler, { passive: false, capture: true });
    return () => el.removeEventListener("wheel", handler, { capture: true });
  }, []);

  // Вычисляем высоту редактора на основе количества строк
  const editorHeight = useMemo(() => {
    const lineHeight = 24;
    const minLines = 5;
    const maxLines = 25;
    const topPadding = 12;
    
    const lines = sql.split('\n').length || 1;
    const actualLines = Math.max(minLines, Math.min(lines, maxLines));
    
    return actualLines * lineHeight + topPadding;
  }, [sql]);

  const executeQuery = async (endpoint: string, check: boolean) => {
    if (!user && !check) {
      setErrorMsg("Авторизуйтесь, чтобы запускать запросы");
      return;
    }
    setIsLoading(true);
    setErrorMsg("");

    try {
      const body = check
        ? { query: sql, checks: checks }
        : { query: sql };

      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const data: QueryResponse = await response.json();

      setExecutionTimeMs(data.execution_time_ms ?? null);
      setMemoryUsageBytes(data.memory_usage_bytes ?? null);
      setReadBytes(data.read_bytes ?? null);
      setReadRows(data.read_rows ?? null);
      setCpuTimeUs(data.cpu_time_us ?? null);

      if (check) {
        const formattedRows = formatRows(data.rows || [], data.columns || []);
        setResult(formattedRows);
        setResultColumns(data.columns || []);

        if (data.status === false) {
          setErrorMsg(data.error_msg || data.decision || "Ошибка проверки");
          setStatus("error");
        } else {
          setErrorMsg("");
          setStatus("success");
        }
      } else {
        if (data.error_msg) {
          setErrorMsg(data.error_msg);
          setResult([]);
          setResultColumns([]);
          setExecutionTimeMs(null);
          setMemoryUsageBytes(null);
          setReadBytes(null);
          setReadRows(null);
          setCpuTimeUs(null);
          setStatus("error");
          setIsLoading(false);
          return;
        }
        setErrorMsg("");
        const formattedRows = formatRows(data.rows || [], data.columns || []);
        setResult(formattedRows);
        setResultColumns(data.columns || []);
        setStatus("idle");
      }
    } catch (err) {
      console.error(err);
      setResult([]);
      setResultColumns([]);
      setExecutionTimeMs(null);
      setMemoryUsageBytes(null);
      setReadBytes(null);
      setReadRows(null);
      setCpuTimeUs(null);
      setStatus("error");
      setErrorMsg("Ошибка при выполнении запроса");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRun = () => {
    executeQuery(`${API_HOST}/api/run-query`, false);
  };

  const handleCheck = () => {
    if (!user) {
      setErrorMsg("Авторизуйтесь, чтобы воспользоваться проверкой");
      return;
    }
    executeQuery(`${API_HOST}/api/check-query-inline`, true);
  };

  const handleReset = () => {
    setSql(initialSql);
    setResult([]);
    setResultColumns([]);
    setErrorMsg("");
    setExecutionTimeMs(null);
    setMemoryUsageBytes(null);
    setReadBytes(null);
    setReadRows(null);
    setCpuTimeUs(null);
    setStatus("idle");
  };

  const hasResult = result.length > 0 || resultColumns.length > 0;
  const isEmpty = result.length === 0 && resultColumns.length === 0;
  const isCodeChanged = sql !== initialSql;

  return (
    <div className="my-6 border border-neutral-border rounded-xl bg-white shadow-sm overflow-hidden">
      {/* Панель инструментов - сверху */}
      <div className="p-2 bg-neutral-light border-b border-neutral-border flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <button
            onClick={handleRun}
            disabled={isLoading}
            className="flex items-center justify-center p-1.5 bg-primary text-white rounded hover:bg-primary-dark active:scale-95 transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
            title="Запуск"
          >
            <Play size={14} />
          </button>
          <button
            onClick={handleCheck}
            disabled={isLoading || !user}
            className="flex items-center justify-center p-1.5 text-white rounded active:scale-95 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: user ? "#007AFF" : "#94a3b8" }}
            title={user ? "Проверить" : "Авторизуйтесь для проверки"}
          >
            <CheckCheck size={14} />
          </button>
          <button
            onClick={handleReset}
            disabled={isLoading || (!hasResult && !isCodeChanged)}
            className="flex items-center justify-center p-1.5 bg-neutral-light border border-neutral-border rounded hover:bg-neutral-border transition disabled:opacity-50 disabled:cursor-not-allowed"
            title="Сбросить код и очистить результаты"
          >
            <RefreshCw size={14} />
          </button>
        </div>
        {formatExecutionTime(executionTimeMs) && (
          <div className="relative group ml-auto">
            <div className="flex items-center gap-1.5 text-xs text-neutral-text/40 cursor-help">
              <span className="text-neutral-text/50">elapsed:</span>
              <span className="font-mono text-neutral-text/50">{formatExecutionTime(executionTimeMs)}</span>
            </div>
            {(formatExecutionTime(executionTimeMs) || formatRowsCount(readRows) || formatBytes(readBytes)) && (
              <div className="absolute right-0 top-full mt-2 hidden group-hover:block bg-neutral-text text-white text-xs rounded-lg px-3 py-2 shadow-xl z-50 whitespace-nowrap pointer-events-none">
                <div className="flex flex-col gap-1">
                  {formatExecutionTime(executionTimeMs) && (
                    <div className="flex items-center gap-2">
                      <span className="text-neutral-300">elapsed:</span>
                      <span className="font-mono">{formatExecutionTime(executionTimeMs)}</span>
                    </div>
                  )}
                  {formatRowsCount(readRows) && (
                    <div className="flex items-center gap-2">
                      <span className="text-neutral-300">read_rows:</span>
                      <span className="font-mono">{formatRowsCount(readRows)}</span>
                    </div>
                  )}
                  {formatBytes(readBytes) && (
                    <div className="flex items-center gap-2">
                      <span className="text-neutral-300">read_bytes:</span>
                      <span className="font-mono">{formatBytes(readBytes)}</span>
                    </div>
                  )}
                </div>
                <div className="absolute -top-1 right-4 w-2 h-2 bg-neutral-text rotate-45"></div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Редактор кода. ref для wheel: скролл страницы, а не редактора. */}
      <div ref={editorBlockRef} className="border-b border-neutral-border relative overflow-hidden" style={{ zIndex: 1 }}>
        <div style={{ height: `${editorHeight}px` }}>
          <Editor value={sql} onChange={setSql} height="100%" disableScroll />
        </div>
      </div>

      {/* Сообщение об ошибке */}
      {errorMsg && (
        <div className="p-3 bg-red-50 border-b border-red-200 text-red-700 text-sm break-words overflow-wrap-anywhere">
          {errorMsg}
        </div>
      )}

      {/* Сообщение об успехе */}
      {status === "success" && !errorMsg && (
        <div className="p-3 bg-green-50 border-b border-green-200 text-green-700 text-sm">
          ✓ Запрос выполнен успешно!
        </div>
      )}

      {/* Результаты или индикатор загрузки */}
      {isLoading ? (
        <>
          <style>{`
            @keyframes bounceBar {
              0%, 100% {
                transform: scaleY(0.3);
                background-color: #0abf53;
              }
              50% {
                transform: scaleY(1);
                background-color: #067934;
              }
            }
            .bounce-bar-compact {
              animation: bounceBar 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) infinite both;
              transform-origin: bottom;
              background-color: #0abf53;
            }
          `}</style>
          <div className="p-4 flex items-center justify-center min-h-[200px]">
            <div className="text-center">
              <div className="flex items-end justify-center gap-1.5 mb-3 h-8">
                <div 
                  className="w-2 h-8 rounded-t bounce-bar-compact" 
                  style={{ animationDelay: "0s" }}
                ></div>
                <div 
                  className="w-2 h-8 rounded-t bounce-bar-compact" 
                  style={{ animationDelay: "0.1s" }}
                ></div>
                <div 
                  className="w-2 h-8 rounded-t bounce-bar-compact" 
                  style={{ animationDelay: "0.2s" }}
                ></div>
                <div 
                  className="w-2 h-8 rounded-t bounce-bar-compact" 
                  style={{ animationDelay: "0.3s" }}
                ></div>
                <div 
                  className="w-2 h-8 rounded-t bounce-bar-compact" 
                  style={{ animationDelay: "0.4s" }}
                ></div>
              </div>
              <p className="text-neutral-text/70 text-sm">Выполнение запроса...</p>
            </div>
          </div>
        </>
      ) : hasResult ? (
        <div className="p-4 overflow-x-auto">
          {result.length > 10 && (
            <div className="mb-3 text-sm text-neutral-text/60 bg-yellow-50 border border-yellow-200 rounded px-3 py-2">
              Показано 10 из {result.length} строк. Добавьте LIMIT в запрос для ограничения результатов.
            </div>
          )}
          <div className="min-w-full">
            <ResultTable
              rows={result.slice(0, 10)}
              columns={resultColumns}
              onCopyMarkdown={() => {
                let md = "| " + resultColumns.join(" | ") + " |\n";
                md += "| " + resultColumns.map(() => "---").join(" | ") + " |\n";
                result.slice(0, 10).forEach((r) => {
                  md += "| " + resultColumns.map((c) => {
                    const formatted = formatCellValue(r[c]);
                    return formatted.replace(/\|/g, "\\|").replace(/\n/g, "<br>");
                  }).join(" | ") + " |\n";
                });
                navigator.clipboard.writeText(md);
                alert("Результат скопирован в Markdown!");
              }}
              isMobile={false}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
};
